// AdminOrdersTable.jsx
import {
  Button,
  Card,
  Checkbox,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { AlertCircle, Send } from "react-feather";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import useAutoOrder from "../../hooks/useAutoOrder";
import { refresh } from "../../store/adminSlice";
import base64ToPdf from "../../utils/base64ToPdf";
import { formatDateTimeWithoutSeconds } from "../../utils/dateFormatter";
import "./AdminOrdersTable.scss";
import { QuestionCircleOutlined } from "@ant-design/icons";
import Details from "./Details";
import OrderStatus, { OrderStatusTitle } from "./OrderStatus";
import ShippingGuideModal from "./ShippingGuideModal";

const { Search } = Input;
const { Option } = Select;

const AdminOrdersTable = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [expandedRowData, setExpandedRowData] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState({});
  const [printLoading, setPrintLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("wege_order_id");
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(
    searchParams.get("needs_attention") === "true",
  );
  const [guideVisible, setGuideVisible] = useState(false);

  const handleAutoOrder = useAutoOrder(() => {
    // Refresh orders after successful auto order
    fetchOrders(
      pagination.current,
      pagination.pageSize,
      searchTerm,
      needsAttentionOnly,
    );
  });

  const currentPage = parseInt(searchParams.get("page")) || 1;
  const currentPageSize = parseInt(searchParams.get("page_size")) || 10;

  const [pagination, setPagination] = useState({
    current: currentPage,
    pageSize: currentPageSize,
    total: 0,
  });

  const fetchOrders = async (
    page = 1,
    pageSize = 5,
    searchValue = searchTerm,
    needsAttention = needsAttentionOnly,
  ) => {
    setLoading(true);
    try {
      let url = `/shipping/api/v1/shipping/admin/wege-orders/?page=${page}&page_size=${pageSize}`;
      if (searchValue) {
        url += `&${filterType}=${encodeURIComponent(searchValue)}`;
      }
      if (needsAttention) {
        url += `&needs_attention=true`;
      }
      const res = await axios.get(url);
      setOrders(res.data.results);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: res.data.count,
      }));
      setSearchParams({
        page,
        page_size: pageSize,
        ...(searchValue && { [filterType]: searchValue }),
        ...(needsAttention ? { needs_attention: "true" } : {}),
      });

      if (searchValue && res.data.results.length === 1) {
        const singleOrderId = res.data.results[0].id;
        setExpandedRowKeys([singleOrderId]);
        if (!expandedRowData[singleOrderId]) {
          fetchOrderDetails(singleOrderId);
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        if (Cookies.get("logss")) {
          dispatch(refresh()).then(() =>
            fetchOrders(page, pageSize, searchValue, needsAttention),
          );
        } else {
          navigate("/");
        }
      } else {
        toast.error(t("toast.fetchOrdersError"));
      }
    }
    setLoading(false);
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      setDetailsLoading(true);
      const res = await axios.get(
        `/shipping/api/v1/shipping/admin/wege-orders/${orderId}/`,
      );
      setExpandedRowData((prev) => ({
        ...prev,
        [orderId]: res.data,
      }));
      setDetailsLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        if (Cookies.get("logss")) {
          dispatch(refresh()).then(() => fetchOrderDetails(orderId));
        } else {
          navigate("/");
        }
      } else {
        toast.error(t("toast.fetchDetailsError"));
        setDetailsLoading(false);
      }
    }
  };

  const fetchOrderStatus = (wegeOrderId, e) => {
    e.stopPropagation();
    const statusPromise = axios
      .post("/alibaba/api/v1/WegeorderLogisticsDetails/", {
        wege_order_id: wegeOrderId,
      })
      .then(() =>
        fetchOrders(pagination.current, pagination.pageSize, searchTerm),
      );
    toast.promise(
      statusPromise,
      {
        loading: t("refreshingStatus"),
        success: t("orderStatusUpdated"),
        error: (err) => {
          if (err.response?.status === 401) {
            if (Cookies.get("logss")) {
              dispatch(refresh()).then(() => fetchOrderStatus(wegeOrderId));
            } else {
              navigate("/");
            }
            return t("authError");
          }
          return err.response?.data?.message || t("toast.fetchOrdersError");
        },
      },
      { duration: 3000 },
    );
  };

  const handlePrint = async (shippingOrderId, e) => {
    if (e?.stopPropagation) {
      e.stopPropagation();
    }
    setPrintLoading((prev) => ({ ...prev, [shippingOrderId]: true }));
    try {
      const res = await axios.post("/shipping/api/v1/get-waybill/", {
        shipping_order_id: shippingOrderId,
      });
      const { type, waybill } = res.data;
      if (type === "link" && waybill) {
        window.open(waybill, "_blank");
      } else if (type === "base64" && waybill) {
        base64ToPdf(waybill, `waybill_${shippingOrderId}.pdf`);
      } else {
        toast.error(t("toast.noWaybill"));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        if (Cookies.get("logss")) {
          dispatch(refresh()).then(() => handlePrint(shippingOrderId));
        } else {
          navigate("/");
        }
      } else {
        toast.error(err.response?.data?.message || t("toast.printError"));
      }
    } finally {
      setPrintLoading((prev) => ({ ...prev, [shippingOrderId]: false }));
    }
  };

  const handlePlaceOrder = (shippingOrderId, providerOverride = null) => {
    const provider =
      providerOverride || selectedProviders[shippingOrderId] || "aramex";
    if (!provider) {
      toast.error(t("placeOrder.missingProvider"));
      return;
    }
    setSelectedProviders((prev) => ({
      ...prev,
      [shippingOrderId]: provider,
    }));
    const placeOrderPromise = axios
      .post("/shipping/api/v1/create-international-order/", {
        shipping_order_id: shippingOrderId,
        provider,
      })
      .then((response) => {
        if (response.data && response.data.label_url) {
          window.open(response.data.label_url, "_blank");
        }
        const currentKey = expandedRowKeys[0];
        if (currentKey) {
          fetchOrderDetails(currentKey);
        }
        return fetchOrders(pagination.current, pagination.pageSize, searchTerm);
      });
    toast.promise(
      placeOrderPromise,
      {
        loading: t("placeOrder.placing"),
        success: () => ` ${t("placeOrder.successMessage")} ${provider}`,
        error: (err) => {
          if (err.response?.status === 401) {
            if (Cookies.get("logss")) {
              dispatch(refresh()).then(() => handlePlaceOrder(shippingOrderId));
            } else {
              navigate("/");
            }
            return t("authError");
          }
          return (
            err.response?.data.error?.message ||
            err.response?.data?.message ||
            t("placeOrder.error")
          );
        },
      },
      { duration: 5000 },
    );
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchOrders(1, pagination.pageSize, value, needsAttentionOnly);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchOrders(1, pagination.pageSize, "", needsAttentionOnly);
  };

  const handleNeedsAttentionChange = (checked) => {
    setNeedsAttentionOnly(checked);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchOrders(1, pagination.pageSize, searchTerm, checked);
  };

  useEffect(() => {
    const initialSearchValue = searchParams.get(filterType) || "";
    const initialNeedsAttention =
      searchParams.get("needs_attention") === "true";
    setSearchTerm(initialSearchValue);
    setNeedsAttentionOnly(initialNeedsAttention);
    fetchOrders(
      pagination.current,
      pagination.pageSize,
      initialSearchValue,
      initialNeedsAttention,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filterType]);

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
    fetchOrders(
      newPagination.current,
      newPagination.pageSize,
      searchTerm,
      needsAttentionOnly,
    );
  };

  const handleRowClick = (record) => {
    const orderId = record.id;
    const isExpanded = expandedRowKeys.includes(orderId);
    if (isExpanded) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys([orderId]);
      if (!expandedRowData[orderId]) {
        fetchOrderDetails(orderId);
      }
    }
  };

  const columns = [
    {
      title: t("table.columns.wegeOrder"),
      dataIndex: "wege_order_id",
      key: "wege_order_id",
      width: 140,
      render: (_, record) => {
        const orderDisplayId =
          record?.wege_order_id || record?.id || t("table.common.na");
        const hasAttention =
          record?.needs_attention ||
          record?.has_attention ||
          record?.attention?.needs_attention;
        return (
          <Space direction="vertical" size={2}>
            <span>{orderDisplayId}</span>
            {hasAttention && (
              <Tag
                color="error"
                style={{
                  fontSize: "11px",
                  lineHeight: "16px",
                  padding: "0 4px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "3px",
                  width: "fit-content",
                }}
              >
                <AlertCircle size={10} />
                {t("shipping.needsAttention", "Needs Attention")}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: t("table.columns.createdAt"),
      dataIndex: "created_at",
      width: 120,
      key: "created_at",
      render: (value) =>
        value ? formatDateTimeWithoutSeconds(value) : t("table.common.na"),
    },
    {
      title: t("table.columns.status"),
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (text) => {
        if (!text) return t("table.common.notAssigned");
        let color = "default";
        const lowerText = text.toLowerCase();
        if (lowerText === "pending") color = "warning";
        else if (lowerText === "shipping" || lowerText === "shipped")
          color = "processing";
        else if (lowerText === "completed" || lowerText === "delivered")
          color = "success";
        else if (lowerText === "arrived")
          color = "cyan"; // Distinct visual state for Partially Arrived
        else if (lowerText === "cancelled" || lowerText === "canceled")
          color = "error";
        return (
          <Tag color={color}>
            {t(`order.status.${lowerText}`, {
              defaultValue: lowerText === "arrived" ? "Partially Arrived" : text,
            })}
          </Tag>
        );
      },
    },
    {
      title: t("table.columns.payment", { defaultValue: "Payment" }),
      key: "payment",
      width: 150,
      render: (_, record) => {
        let methodColor = "default";
        const methodText = record.payment_method;
        if (methodText) {
          const lowerText = methodText.toLowerCase();
          if (lowerText === "tabby") methodColor = "purple";
          if (lowerText === "tamara") methodColor = "orange";
          else if (lowerText === "stripe") methodColor = "cyan";
          else if (lowerText === "cash" || lowerText === "cod")
            methodColor = "gold";
        }
        return (
          <Space size={4} align="center">
            {methodText ? (
              <Tag color={methodColor} style={{ marginRight: 0 }}>
                {methodText}
              </Tag>
            ) : (
              <Tag style={{ marginRight: 0 }}>{t("table.common.na")}</Tag>
            )}
            <Tag
              color={record.is_paid ? "success" : "error"}
              style={{ marginRight: 0 }}
            >
              {record.is_paid
                ? t("table.common.paid")
                : t("table.common.unpaid")}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: t("table.columns.email"),
      dataIndex: "email",
      key: "email",
      render: (text) => text || t("table.common.na"),
    },
    {
      title: t("table.columns.total"),
      dataIndex: "total",
      key: "total",
      render: (value) => {
        if (value === null || value === undefined) {
          return t("table.common.na");
        }
        // Format number with commas and add AED suffix
        const formattedValue = Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return `${formattedValue} AED`;
      },
    },
    {
      title: t("table.columns.estimatedShipping", {
        defaultValue: "Est. Shipping",
      }),
      dataIndex: "total_estimated_shipping_cost",
      key: "total_estimated_shipping_cost",
      render: (value, record) => {
        if (value === null || value === undefined) {
          return t("table.common.na");
        }
        const formattedValue = Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return `${formattedValue} ${record.total_estimated_shipping_cost_currency || "AED"}`;
      },
    },
    {
      title: <OrderStatusTitle />,
      key: "alibaba_orders",
      render: (_, record) => (
        <OrderStatus record={record} onRefresh={fetchOrderStatus} />
      ),
    },
    {
      title: t("table.columns.actions"),
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<Send size={14} />}
          onClick={(e) => {
            e.stopPropagation();
            const orderId = record?.wege_order_id || record?.id;
            if (orderId) {
              handleAutoOrder(orderId);
            }
          }}
          title={t("table.actions.autoOrder", { defaultValue: "Auto Order" })}
        />
      ),
    },
  ];

  const handleRefresh = () => {
    fetchOrders(
      pagination.current,
      pagination.pageSize,
      searchTerm,
      needsAttentionOnly,
    );
    const currentKey = expandedRowKeys[0];
    if (currentKey) {
      fetchOrderDetails(currentKey);
    }
  };

  const expandedRowRender = (record) => (
    <Details
      details={expandedRowData[record.id]}
      wegeOrderId={record.id}
      detailsLoading={detailsLoading}
      searchTerm={searchTerm}
      onRefresh={handleRefresh}
      selectedProviders={selectedProviders}
      setSelectedProviders={setSelectedProviders}
      onPlaceOrder={handlePlaceOrder}
      onPrint={handlePrint}
      printLoading={printLoading}
      onOpenGuide={() => setGuideVisible(true)}
    />
  );

  return (
    <div className="admin-orders-container">
      <Card
        bordered={false}
        className="admin-orders-card"
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t("dashboard.ordersTitle", "Orders Management")}
          </Typography.Title>
        }
        extra={
          <Button
            type="primary"
            ghost
            icon={<QuestionCircleOutlined />}
            onClick={() => setGuideVisible(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: 500,
              borderRadius: "6px",
            }}
          >
            {t("guide.buttonTitle", "Shipping Process Guide")}
          </Button>
        }
      >
        <div className="table-header-controls">
          <Space size="middle" wrap align="center">
            <Select
              value={filterType}
              onChange={handleFilterChange}
              style={{ width: 200 }}
              size="large"
            >
              <Option value="wege_order_id">
                {t("orders1688.filter.wegeOrderIdOption")}
              </Option>
              <Option value="logistics_bill_no">
                {t("orders1688.filter.trackingNumberOption")}
              </Option>
            </Select>
            <Search
              placeholder={
                filterType === "wege_order_id"
                  ? t("searchByWegeOrder")
                  : t("searchByLogisticsBill")
              }
              allowClear
              enterButton={t("Search")}
              size="large"
              onSearch={handleSearch}
              style={{ width: 350 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setExpandedRowKeys([])}
            />
            <Checkbox
              checked={needsAttentionOnly}
              onChange={(e) => handleNeedsAttentionChange(e.target.checked)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                userSelect: "none",
                cursor: "pointer",
              }}
            >
              <Tag
                color={needsAttentionOnly ? "error" : "default"}
                style={{
                  cursor: "pointer",
                  marginRight: 0,
                  fontSize: "13px",
                  padding: "4px 8px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  borderRadius: "4px",
                }}
              >
                <AlertCircle size={14} />
                {t("shipping.needsAttentionOnly", "Needs attention only")}
              </Tag>
            </Checkbox>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={orders}
          loading={loading}
          rowKey="id"
          className="admin-orders-table"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            className: "admin-orders-pagination",
          }}
          onChange={handleTableChange}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            expandIcon: () => null,
            expandRowByClick: true,
            fixed: true,
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
        />
      </Card>
      <ShippingGuideModal
        visible={guideVisible}
        onClose={() => setGuideVisible(false)}
      />
    </div>
  );
};

export default AdminOrdersTable;
